import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

// Character pose mappings for different message types
export const CHARACTER_POSES = {
  welcome: 'waving',
  greeting: 'waving',
  tip: 'idea',
  suggestion: 'idea',
  success: 'handsInAir',
  celebration: 'handsInAir',
  achievement: 'handsInAir',
  encouragement: 'handsOnHips',
  motivate: 'handsOnHips',
  warning: 'grimace',
  error: 'grimace',
  reminder: 'watch',
  time: 'watch',
  deadline: 'watch',
  attention: 'pointRight',
  focus: 'pointRight',
  positive: 'smiling',
  default: 'smiling',
  highRisk: 'highRisk',
};

// Pre-loaded character images
export const CHARACTER_IMAGES = {
  waving: require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_Waving_v01_SL.png'),
  smiling: require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_Smiling_v01_SL.png'),
  idea: require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_Idea_v01_SL.png'),
  handsInAir: require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_HandsInAir_v01_SL.png'),
  handsOnHips: require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_HandsOnHips_v01_SL.png'),
  grimace: require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_Grimace_v01_SL.png'),
  watch: require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_Watch_v01_SL.png'),
  pointLeft: require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_PointLeft_v01_SL.png'),
  pointRight: require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_PointRight_v01_SL.png'),
  highRisk: require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_HighRisk_v01_SL.png'),
};

const CharacterPopupContext = createContext();

export const useCharacterPopup = () => {
  const context = useContext(CharacterPopupContext);
  if (!context) {
    throw new Error('useCharacterPopup must be used within a CharacterPopupProvider');
  }
  return context;
};

export const CharacterPopupProvider = ({ children }) => {
  const [popups, setPopups] = useState([]);
  const popupIdRef = useRef(0);
  const cooldownRef = useRef({});

  // Show a character popup
  const showPopup = useCallback(({
    title,
    message,
    type = 'default', // welcome, tip, success, warning, reminder, encouragement, etc.
    duration = 5000, // Auto-dismiss after 5 seconds (0 = manual dismiss only)
    position = 'bottom-right', // bottom-left, bottom-right, top-left, top-right
    pose = null, // Override the auto-selected pose
    id = null, // Optional custom ID for preventing duplicates
    cooldown = 0, // Minimum time before showing the same popup again (by id)
  }) => {
    const popupId = id || `popup-${++popupIdRef.current}`;
    
    // Check cooldown if id is provided
    if (id && cooldown > 0) {
      const lastShown = cooldownRef.current[id];
      if (lastShown && Date.now() - lastShown < cooldown) {
        return null; // Skip, still in cooldown
      }
      cooldownRef.current[id] = Date.now();
    }

    // Don't add duplicate popups by id
    setPopups(prev => {
      if (prev.some(p => p.id === popupId)) {
        return prev;
      }
      
      const newPopup = {
        id: popupId,
        title,
        message,
        type,
        duration,
        position,
        pose: pose || CHARACTER_POSES[type] || CHARACTER_POSES.default,
        createdAt: Date.now(),
      };
      
      return [...prev, newPopup];
    });

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        dismissPopup(popupId);
      }, duration);
    }

    return popupId;
  }, []);

  // Dismiss a specific popup
  const dismissPopup = useCallback((popupId) => {
    setPopups(prev => prev.filter(p => p.id !== popupId));
  }, []);

  // Dismiss all popups
  const dismissAllPopups = useCallback(() => {
    setPopups([]);
  }, []);

  // Convenience methods for different popup types
  const showWelcome = useCallback((title, message, options = {}) => {
    return showPopup({ title, message, type: 'welcome', ...options });
  }, [showPopup]);

  const showTip = useCallback((title, message, options = {}) => {
    return showPopup({ title, message, type: 'tip', ...options });
  }, [showPopup]);

  const showSuccess = useCallback((title, message, options = {}) => {
    return showPopup({ title, message, type: 'success', ...options });
  }, [showPopup]);

  const showWarning = useCallback((title, message, options = {}) => {
    return showPopup({ title, message, type: 'warning', ...options });
  }, [showPopup]);

  const showReminder = useCallback((title, message, options = {}) => {
    return showPopup({ title, message, type: 'reminder', ...options });
  }, [showPopup]);

  const showEncouragement = useCallback((title, message, options = {}) => {
    return showPopup({ title, message, type: 'encouragement', ...options });
  }, [showPopup]);

  const showCelebration = useCallback((title, message, options = {}) => {
    return showPopup({ title, message, type: 'celebration', duration: 6000, ...options });
  }, [showPopup]);

  return (
    <CharacterPopupContext.Provider value={{
      popups,
      showPopup,
      dismissPopup,
      dismissAllPopups,
      showWelcome,
      showTip,
      showSuccess,
      showWarning,
      showReminder,
      showEncouragement,
      showCelebration,
      CHARACTER_IMAGES,
      CHARACTER_POSES,
    }}>
      {children}
    </CharacterPopupContext.Provider>
  );
};

export default CharacterPopupContext;
